import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';

const TradingModeContext = createContext(null);

export function TradingModeProvider({ children }) {
  const [mode, setMode] = useState('paper');        // 'paper' | 'live'
  const [exchange, setExchange] = useState(null);    // e.g. 'binance'
  const [exchangeName, setExchangeName] = useState(null);
  const [connectedExchanges, setConnectedExchanges] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMode = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const [modeRes, exchRes] = await Promise.all([
        axios.get(`${API}/trading-mode`, { headers }),
        axios.get(`${API}/exchanges/connected`, { headers }).catch(() => ({ data: { exchanges: [] } }))
      ]);
      setMode(modeRes.data.mode || 'paper');
      setExchange(modeRes.data.exchange || null);
      setExchangeName(modeRes.data.exchange_name || null);
      setConnectedExchanges(exchRes.data.exchanges || []);
    } catch {
      // defaults
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchMode(); }, [fetchMode]);

  const switchMode = useCallback(async (newMode, newExchange) => {
    try {
      const res = await axios.put(`${API}/trading-mode`, {
        mode: newMode,
        exchange: newExchange || null
      }, { headers });

      setMode(res.data.mode);
      setExchange(res.data.exchange);
      setExchangeName(res.data.exchange_name);
      return res.data;
    } catch (err) {
      throw err;
    }
  }, [token]);

  const isLive = mode === 'live';

  return (
    <TradingModeContext.Provider value={{
      mode, exchange, exchangeName, isLive, loading,
      connectedExchanges, switchMode, refresh: fetchMode
    }}>
      {children}
    </TradingModeContext.Provider>
  );
}

export function useTradingMode() {
  const ctx = useContext(TradingModeContext);
  if (!ctx) throw new Error('useTradingMode must be used within TradingModeProvider');
  return ctx;
}
